import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface PaginationFooterProps {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  pageSizeOptions?: number[];
  isLoading?: boolean;
  className?: string;
}

export const PaginationFooter: React.FC<PaginationFooterProps> = ({
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  isLoading = false,
  className = "",
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Compute start and end indices for display indicator
  const fromIndex = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const toIndex = Math.min(currentPage * pageSize, totalCount);

  // Generate numbered pages with sliding window / ellipsis
  const pageNumbers = useMemo(() => {
    // If 7 or fewer pages, show all pages
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    if (currentPage <= 4) {
      // Near beginning: 1, 2, 3, 4, 5, '...', totalPages
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      // Near end: 1, '...', totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages
      pages.push(1);
      pages.push("...");
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Middle: 1, '...', currentPage-1, currentPage, currentPage+1, '...', totalPages
      pages.push(1);
      pages.push("...");
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages]);

  const handlePageSelect = (page: number) => {
    if (page === currentPage || page < 1 || page > totalPages || isLoading) return;
    onPageChange(page);
  };

  const handlePageSizeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    if (!isNaN(newSize)) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <div
      className={`cht-flex cht-flex-col sm:cht-flex-row cht-items-center cht-justify-between cht-gap-4 cht-px-6 cht-py-4 cht-border-t cht-border-gray-100 cht-bg-white/50 ${className}`}
    >
      {/* Left: Page Size Selector & Text Indicator */}
      <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3 cht-text-xs cht-text-gray-500">
        <div className="cht-flex cht-items-center cht-gap-2">
          <span className="cht-font-medium">Rows per page:</span>
          <select
            value={pageSize}
            onChange={handlePageSizeSelect}
            disabled={isLoading}
            className="cht-bg-white cht-border cht-border-gray-200 cht-rounded-xl cht-px-2.5 cht-py-1.5 cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 focus:cht-border-[#3e57da] cht-cursor-pointer disabled:cht-opacity-50"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <span className="cht-hidden sm:cht-inline cht-text-gray-300">|</span>

        <span className="cht-font-medium">
          Showing <span className="cht-font-black cht-text-gray-800">{fromIndex}</span> to{" "}
          <span className="cht-font-black cht-text-gray-800">{toIndex}</span> of{" "}
          <span className="cht-font-black cht-text-gray-800">{totalCount}</span> entries
        </span>
      </div>

      {/* Right: Pagination Controls */}
      <div className="cht-flex cht-items-center cht-gap-1.5">
        {/* Previous Button */}
        <button
          type="button"
          onClick={() => handlePageSelect(currentPage - 1)}
          disabled={currentPage <= 1 || isLoading}
          className="cht-p-2 cht-rounded-xl cht-border cht-border-gray-200 cht-text-gray-600 hover:cht-bg-gray-100 disabled:cht-opacity-35 disabled:cht-cursor-not-allowed cht-transition-colors"
          title="Previous Page"
          aria-label="Previous Page"
        >
          <ChevronLeft size={16} />
        </button>

        {/* Numbered Page Buttons with Sliding Window */}
        <div className="cht-flex cht-items-center cht-gap-1">
          {pageNumbers.map((pageNum, idx) => {
            if (typeof pageNum === "string") {
              return (
                <span
                  key={`ellipsis-${idx}`}
                  className="cht-w-8 cht-h-8 cht-flex cht-items-center cht-justify-center cht-text-xs cht-font-bold cht-text-gray-400"
                >
                  •••
                </span>
              );
            }

            const isCurrent = pageNum === currentPage;

            return (
              <button
                type="button"
                key={pageNum}
                onClick={() => handlePageSelect(pageNum)}
                disabled={isLoading}
                className={`cht-w-8 cht-h-8 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-xs cht-font-black cht-transition-all ${
                  isCurrent
                    ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                    : "cht-text-gray-600 hover:cht-bg-gray-100 cht-border cht-border-transparent hover:cht-border-gray-200"
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          type="button"
          onClick={() => handlePageSelect(currentPage + 1)}
          disabled={currentPage >= totalPages || isLoading}
          className="cht-p-2 cht-rounded-xl cht-border cht-border-gray-200 cht-text-gray-600 hover:cht-bg-gray-100 disabled:cht-opacity-35 disabled:cht-cursor-not-allowed cht-transition-colors"
          title="Next Page"
          aria-label="Next Page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default PaginationFooter;
